import { useCallback, useMemo, useState } from 'react';
import type { TableType } from '../../../models/ContentManagementInterface';
import type { TableCellStyle, TableRow } from '../../template/Table';
import {
  deleteColumn,
  deleteRow,
  gridAnchors,
  insertColumn,
  insertRow,
  mergeCells,
  parseClipboardTable,
  setCellContent,
  setCellStyle,
  setRowStyle,
  splitCell,
  tableSize,
  toGrid,
} from './tableModel';

export type CellPos = [number, number];

export interface SelectionRect {
  r1: number;
  c1: number;
  r2: number;
  c2: number;
}

/**
 * State + actions for the Table editor. Every mutation goes through a pure
 * `tableModel` op and is committed as normalized rows, so the block can never
 * hold anything the `TableContent` schema can't express.
 */
export function useTableEditor(
  properties: TableType['properties'],
  onChange: (props: TableType['properties']) => void,
) {
  const { rows } = properties;
  const grid = useMemo(() => toGrid(rows), [rows]);
  const anchorsByRow = useMemo(() => gridAnchors(grid), [grid]);
  const { rowCount, columnCount } = useMemo(() => tableSize(rows), [rows]);

  const [anchor, setAnchor] = useState<CellPos | null>(null);
  const [focus, setFocus] = useState<CellPos | null>(null);

  const commit = useCallback(
    (next: TableRow[]) => onChange({ ...properties, rows: next }),
    [onChange, properties],
  );

  const clearSelection = useCallback(() => {
    setAnchor(null);
    setFocus(null);
  }, []);

  const selectionRect: SelectionRect | null = useMemo(() => {
    if (!anchor || !focus) return null;
    return {
      r1: Math.min(anchor[0], focus[0]),
      c1: Math.min(anchor[1], focus[1]),
      r2: Math.max(anchor[0], focus[0]),
      c2: Math.max(anchor[1], focus[1]),
    };
  }, [anchor, focus]);

  return {
    grid,
    anchorsByRow,
    rowCount,
    columnCount,
    anchor,
    focus,
    selectionRect,
    setAnchor,
    setFocus,
    clearSelection,
    updateCell: (r: number, c: number, html: string) => commit(setCellContent(rows, r, c, html)),
    setRowStyle: (r: number, style: TableCellStyle) => commit(setRowStyle(rows, r, style)),
    setCellStyle: (r: number, c: number, style: TableCellStyle) =>
      commit(setCellStyle(rows, r, c, style)),
    addRow: (at: number) => commit(insertRow(rows, at)),
    removeRow: (r: number) => commit(deleteRow(rows, r)),
    addColumn: (at: number) => commit(insertColumn(rows, at)),
    removeColumn: (c: number) => commit(deleteColumn(rows, c)),
    mergeSelection: () => {
      if (anchor && focus) {
        commit(mergeCells(rows, anchor, focus));
        clearSelection();
      }
    },
    splitAt: (r: number, c: number) => {
      commit(splitCell(rows, r, c));
      clearSelection();
    },
    replaceFromClipboard: (html: string, text: string): boolean => {
      const parsed = parseClipboardTable(html, text);
      if (!parsed || parsed.length === 0) return false;
      commit(parsed);
      clearSelection();
      return true;
    },
  };
}
